import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Pencil, Trash2, GripVertical } from 'lucide-react';
import { fleet, telemetry } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useMqtt } from '../hooks/useMqtt';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { ConflictDialog } from '../dashboards/ConflictDialog';
import { NameDialog } from '../dashboards/NameDialog';
import { DashboardGrid } from '../dashboards/grid';
import { PanelEditor } from '../dashboards/PanelEditor';
import { PanelErrorBoundary } from '../dashboards/PanelErrorBoundary';
import { draftToPanel, resolveConflict, type PanelDraft } from '../dashboards/editor-state';
import { TIME_RANGES } from '../dashboards/mapping';
import { ChartPanel } from '../dashboards/panels/ChartPanel';
import { FaultsPanel } from '../dashboards/panels/FaultsPanel';
import { GaugePanel } from '../dashboards/panels/GaugePanel';
import { LivePanel } from '../dashboards/panels/LivePanel';
import { StatPanel } from '../dashboards/panels/StatPanel';
import type { DashboardDoc, DashboardRecord, Panel } from '../dashboards/types';
import {
  isConflict,
  useCreateDashboard,
  useDashboard,
  useDashboardList,
  useDeleteDashboard,
  useSaveDashboard,
} from '../dashboards/useDashboards';

const NEW_DOC: DashboardDoc = {
  v: 1,
  name: 'New dashboard',
  scope: 'user',
  timeRange: '6h',
  refresh: 30,
  panels: [],
};

const REFRESH_OPTIONS: { label: string; value: number }[] = [
  { label: 'off', value: 0 },
  { label: '30s', value: 30 },
  { label: '60s', value: 60 },
  { label: '5m', value: 300 },
];

// UI mirror of the server's authoritative ORG_WRITE_ROLES
// (server/src/routes/dashboards.ts) — gates editing affordances only. The
// server enforces the real boundary; keep these two lists in lockstep.
const EDITOR_ROLES = ['admin', 'engineer'];

function toDoc(rec: DashboardRecord): DashboardDoc {
  return {
    v: rec.v,
    name: rec.name,
    scope: rec.scope,
    timeRange: rec.timeRange,
    refresh: rec.refresh,
    panels: rec.panels,
  };
}

function nextFreePosition(panels: Panel[]): { x: number; y: number; w: number; h: number } {
  return {
    x: 0,
    y: panels.length ? Math.max(...panels.map((p) => p.y + p.h)) : 0,
    w: 6,
    h: 4,
  };
}

interface EditorState {
  open: boolean;
  panel: Panel | null;
}

export default function DashboardsPage() {
  const { user } = useAuth();
  const { id: routeId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // The URL is the source of truth for the selected dashboard.
  const selectedId = routeId ?? null;

  const [working, setWorking] = useState<DashboardDoc | null>(null);
  const [baseline, setBaseline] = useState<DashboardRecord | null>(null);
  const [dirty, setDirty] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [editor, setEditor] = useState<EditorState>({ open: false, panel: null });
  const [nameDialog, setNameDialog] = useState(false);
  const [renaming, setRenaming] = useState(false);

  const metricsQuery = useQuery({
    queryKey: ['telemetry', 'metrics'],
    queryFn: telemetry.listMetrics,
  });
  const devicesQuery = useQuery({ queryKey: ['fleet', 'devices'], queryFn: fleet.listDevices });
  const metrics = metricsQuery.data?.metrics ?? [];

  const list = useDashboardList();
  const remote = useDashboard(selectedId);
  const create = useCreateDashboard();
  const save = useSaveDashboard();
  const remove = useDeleteDashboard();

  // ── Page-level single MQTT connection (stable live key prevents reconnect
  // churn on every edit — only the SET of live serials matters). ───────────
  const liveKey = useMemo(
    () =>
      [
        ...new Set(
          (working?.panels ?? [])
            .filter((p): p is Extract<Panel, { type: 'live' }> => p.type === 'live')
            .map((p) => p.serial),
        ),
      ]
        .sort()
        .join('|'),
    [working],
  );
  const liveDevices = useMemo(() => {
    const serials = new Set(liveKey ? liveKey.split('|') : []);
    return (devicesQuery.data ?? []).filter((a) => serials.has(a.serial));
  }, [liveKey, devicesQuery.data]);
  const { isConnected: mqttConnected, telemetry: liveTelemetry } = useMqtt(liveDevices);

  // ── Initial auto-selection: on bare /dashboards (no routeId), redirect to
  // the first dashboard's URL (My before Org). Empty list → stay put (CTA). ──
  useEffect(() => {
    if (routeId || !list.data) return;
    const all = list.data.dashboards;
    if (all.length === 0) return;
    const mine = all.filter((d) => d.scope === 'user' && d.created_by === user?.sub);
    const org = all.filter((d) => !(d.scope === 'user' && d.created_by === user?.sub));
    const first = mine[0] ?? org[0];
    if (first) navigate(`/dashboards/${first.id}`, { replace: true });
  }, [list.data, routeId, user?.sub, navigate]);

  // ── Dirty reset on route change: switching dashboards via URL must not carry
  // the previous dirty flag (the hydration effect then loads the new remote). ─
  useEffect(() => {
    setDirty(false);
  }, [routeId]);

  // ── Hydration: copy the fresh record into working+baseline when clean. ───
  useEffect(() => {
    if (!remote.data || dirty) return;
    setWorking(toDoc(remote.data));
    setBaseline(remote.data);
  }, [remote.data, dirty]);

  const canEdit =
    working?.scope === 'user' || EDITOR_ROLES.includes(user?.role ?? '');

  const dashboardList = list.data?.dashboards ?? [];
  const myDashboards = dashboardList.filter(
    (d) => d.scope === 'user' && d.created_by === user?.sub,
  );
  const orgDashboards = dashboardList.filter(
    (d) => !(d.scope === 'user' && d.created_by === user?.sub),
  );

  const updateWorking = (patch: Partial<DashboardDoc>) => {
    setWorking((w) => (w ? { ...w, ...patch } : w));
    setDirty(true);
  };

  const createNamed = (name: string) => {
    create.mutate(
      { ...NEW_DOC, name },
      {
        onSuccess: (rec) => {
          setDirty(false);
          navigate(`/dashboards/${rec.id}`);
        },
      },
    );
  };

  const removePanel = (id: string) => {
    if (!working) return;
    updateWorking({ panels: working.panels.filter((p) => p.id !== id) });
  };

  const handleApply = (draft: PanelDraft) => {
    if (!working) return;
    if (editor.panel === null) {
      // Add mode — append a freshly positioned panel.
      const panel = draftToPanel(
        draft,
        crypto.randomUUID(),
        nextFreePosition(working.panels),
        metrics,
      );
      updateWorking({ panels: [...working.panels, panel] });
    } else {
      // Edit mode — replace by id, preserving the existing geometry.
      const target = editor.panel;
      const replacement = draftToPanel(
        draft,
        target.id,
        { x: target.x, y: target.y, w: target.w, h: target.h },
        metrics,
      );
      updateWorking({
        panels: working.panels.map((p) => (p.id === target.id ? replacement : p)),
      });
    }
    setEditor({ open: false, panel: null });
  };

  const handleSave = () => {
    if (!working || !selectedId || !baseline) return;
    setSaveError(null);
    save.mutate(
      { id: selectedId, doc: working, expectedUpdatedAt: baseline.updated_at },
      {
        onSuccess: (rec) => {
          setDirty(false);
          setBaseline(rec);
          setWorking(toDoc(rec));
        },
        onError: (err) => {
          if (isConflict(err)) setConflict(true);
          else setSaveError(err instanceof Error ? err.message : 'Failed to save dashboard');
        },
      },
    );
  };

  const handleConflictReload = async () => {
    if (!selectedId) return;
    const fresh = (await remote.refetch()).data;
    if (!fresh) return;
    resolveConflict('reload', fresh.updated_at); // { action: 'reload' }
    setWorking(toDoc(fresh));
    setBaseline(fresh);
    setDirty(false);
    setConflict(false);
  };

  const handleConflictOverwrite = async () => {
    if (!selectedId || !working) return;
    const fresh = (await remote.refetch()).data;
    if (!fresh) return;
    const resolution = resolveConflict('overwrite', fresh.updated_at);
    if (resolution.action !== 'overwrite') return;
    save.mutate(
      { id: selectedId, doc: working, expectedUpdatedAt: resolution.retryWith },
      {
        onSuccess: (rec) => {
          setDirty(false);
          setBaseline(rec);
          setWorking(toDoc(rec));
          setConflict(false);
        },
        onError: (err) => {
          setConflict(false);
          setSaveError(err instanceof Error ? err.message : 'Failed to overwrite dashboard');
        },
      },
    );
  };

  const handleDelete = () => {
    if (!selectedId) return;
    remove.mutate(selectedId, {
      onSuccess: () => {
        setConfirmDelete(false);
        navigate('/dashboards');
        setWorking(null);
        setBaseline(null);
        setDirty(false);
      },
    });
  };

  // ── Per-panel renderer: header bar (title, drag handle, edit/remove) +
  // the content renderer wrapped in an error boundary. ─────────────────────
  const renderPanel = (p: Panel) => {
    const tr = working?.timeRange ?? '6h';
    const refreshMs = (working?.refresh ?? 0) * 1000;
    const unit =
      'metric' in p ? metrics.find((m) => m.id === p.metric)?.unit : undefined;

    let content: React.ReactNode;
    switch (p.type) {
      case 'chart':
        content = <ChartPanel panel={p} timeRange={tr} refreshMs={refreshMs} />;
        break;
      case 'stat':
        content = <StatPanel panel={p} timeRange={tr} refreshMs={refreshMs} unit={unit} />;
        break;
      case 'gauge':
        content = <GaugePanel panel={p} timeRange={tr} refreshMs={refreshMs} unit={unit} />;
        break;
      case 'faults':
        content = <FaultsPanel panel={p} timeRange={tr} refreshMs={refreshMs} />;
        break;
      case 'live':
        content = (
          <LivePanel
            panel={p}
            telemetry={liveTelemetry}
            isConnected={mqttConnected}
            device={devicesQuery.data?.find((a) => a.serial === p.serial)}
          />
        );
        break;
    }

    return (
      <div className="flex h-full flex-col">
        <div className="flex items-center gap-1 border-b border-surface-4 px-2 py-1">
          <span
            className="drag-handle cursor-move text-gray-600 hover:text-gray-400"
            aria-hidden="true"
          >
            <GripVertical size={12} />
          </span>
          <span className="flex-1 truncate text-xs font-medium text-gray-300">{p.title}</span>
          {canEdit && (
            <>
              <button
                type="button"
                aria-label={`Edit ${p.title}`}
                className="p-0.5 text-gray-500 hover:text-gray-300"
                onClick={() => setEditor({ open: true, panel: p })}
              >
                <Pencil size={12} />
              </button>
              <button
                type="button"
                aria-label={`Remove ${p.title}`}
                className="p-0.5 text-gray-500 hover:text-red-400"
                onClick={() => removePanel(p.id)}
              >
                <Trash2 size={12} />
              </button>
            </>
          )}
        </div>
        <div className="min-h-0 flex-1">
          <PanelErrorBoundary title={p.title}>{content}</PanelErrorBoundary>
        </div>
      </div>
    );
  };

  // ── Render ───────────────────────────────────────────────────────────────
  if (list.isLoading) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold text-white">Dashboards</h1>
        <p className="mt-2 text-sm text-gray-500">Loading…</p>
      </div>
    );
  }

  // Empty-state: no dashboards at all → ONE centered CTA (no toolbar button).
  if (dashboardList.length === 0) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold text-white">Dashboards</h1>
        <div className="mt-16 flex flex-col items-center justify-center gap-3 text-center">
          <p className="text-sm text-gray-500">No dashboards yet.</p>
          <button
            type="button"
            className="btn-primary text-sm"
            onClick={() => setNameDialog(true)}
            disabled={create.isPending}
          >
            New dashboard
          </button>
        </div>

        <NameDialog
          open={nameDialog}
          title="New dashboard"
          initialValue="New dashboard"
          confirmLabel="Create"
          onConfirm={(name) => {
            setNameDialog(false);
            createNamed(name);
          }}
          onCancel={() => setNameDialog(false)}
        />
      </div>
    );
  }

  const hasPanels = (working?.panels.length ?? 0) > 0;

  return (
    <div className="flex h-full flex-col p-6">
      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold text-white">Dashboards</h1>

        {/* Inline rename of the CURRENT dashboard — click-to-edit when editable,
            plain text when read-only. */}
        {working &&
          (canEdit ? (
            <button
              type="button"
              aria-label="Rename dashboard"
              className="group flex items-center gap-1.5 text-lg font-medium text-gray-300 hover:text-white"
              onClick={() => setRenaming(true)}
            >
              <span className="truncate">{working.name}</span>
              <Pencil
                size={14}
                className="shrink-0 text-gray-600 group-hover:text-kinetica-400"
              />
            </button>
          ) : (
            <span className="text-lg font-medium text-gray-300">{working.name}</span>
          ))}

        {/* Switcher */}
        <div className="flex flex-wrap items-center gap-1">
          {myDashboards.length > 0 && (
            <span className="text-xs uppercase tracking-wide text-gray-600">My</span>
          )}
          {myDashboards.map((d) => (
            <button
              key={d.id}
              type="button"
              aria-current={d.id === selectedId ? 'true' : undefined}
              onClick={() => navigate(`/dashboards/${d.id}`)}
              className={`rounded px-2 py-1 text-sm ${
                d.id === selectedId
                  ? 'bg-kinetica-600/20 text-white'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              {d.name}
            </button>
          ))}
          {orgDashboards.length > 0 && (
            <span className="ml-2 text-xs uppercase tracking-wide text-gray-600">Org</span>
          )}
          {orgDashboards.map((d) => (
            <button
              key={d.id}
              type="button"
              aria-current={d.id === selectedId ? 'true' : undefined}
              onClick={() => navigate(`/dashboards/${d.id}`)}
              className={`rounded px-2 py-1 text-sm ${
                d.id === selectedId
                  ? 'bg-kinetica-600/20 text-white'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              {d.name}
            </button>
          ))}
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {/* Time range */}
          <select
            aria-label="Time range"
            className="input text-sm"
            value={working?.timeRange ?? '6h'}
            disabled={!working}
            onChange={(e) => updateWorking({ timeRange: e.target.value })}
          >
            {TIME_RANGES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>

          {/* Refresh */}
          <select
            aria-label="Refresh"
            className="input text-sm"
            value={working?.refresh ?? 0}
            disabled={!working}
            onChange={(e) => updateWorking({ refresh: Number(e.target.value) })}
          >
            {REFRESH_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>

          {/* Add panel — toolbar button only when panels already exist. */}
          {canEdit && hasPanels && (
            <button
              type="button"
              className="btn-secondary text-sm"
              onClick={() => setEditor({ open: true, panel: null })}
            >
              Add panel
            </button>
          )}

          {canEdit && (
            <button
              type="button"
              className="btn-primary text-sm disabled:cursor-not-allowed disabled:opacity-50"
              disabled={!dirty || !canEdit || save.isPending}
              onClick={handleSave}
            >
              Save
            </button>
          )}

          {canEdit && (
            <button
              type="button"
              aria-label="Delete dashboard"
              className="btn-secondary text-sm"
              onClick={() => setConfirmDelete(true)}
            >
              <Trash2 size={14} />
            </button>
          )}

          <button
            type="button"
            className="btn-secondary text-sm"
            onClick={() => setNameDialog(true)}
            disabled={create.isPending}
          >
            New dashboard
          </button>
        </div>
      </div>

      {saveError && <p className="mb-3 text-sm text-red-400">{saveError}</p>}

      {/* Body */}
      <div className="min-h-0 flex-1">
        {remote.isLoading || !working ? (
          <p className="text-sm text-gray-500">Loading dashboard…</p>
        ) : !hasPanels ? (
          // Empty-state: zero panels → ONE centered CTA (toolbar Add panel hidden).
          <div className="mt-16 flex flex-col items-center justify-center gap-3 text-center">
            <p className="text-sm text-gray-500">This dashboard has no panels.</p>
            {canEdit && (
              <button
                type="button"
                className="btn-primary text-sm"
                onClick={() => setEditor({ open: true, panel: null })}
              >
                Add panel
              </button>
            )}
          </div>
        ) : (
          <DashboardGrid
            panels={working.panels}
            editable={canEdit}
            onLayoutChange={(panels) => {
              setWorking({ ...working, panels });
              setDirty(true);
            }}
            renderPanel={renderPanel}
          />
        )}
      </div>

      {/* Panel editor drawer */}
      <PanelEditor
        open={editor.open}
        initial={editor.panel}
        metrics={metrics}
        devices={devicesQuery.data ?? []}
        timeRange={working?.timeRange ?? '6h'}
        onApply={handleApply}
        onClose={() => setEditor({ open: false, panel: null })}
      />

      {/* Conflict modal — dismissing (backdrop/Escape/close) KEEPS local edits;
          only the explicit Reload/Overwrite buttons resolve the conflict. */}
      <ConflictDialog
        open={conflict}
        loading={save.isPending}
        onReload={() => void handleConflictReload()}
        onOverwrite={() => void handleConflictOverwrite()}
        onDismiss={() => setConflict(false)}
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        open={confirmDelete}
        title="Delete dashboard"
        message={`Delete "${working?.name ?? 'this dashboard'}"? This cannot be undone.`}
        severity="danger"
        confirmLabel="Delete"
        loading={remove.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />

      {/* Name-on-create dialog */}
      <NameDialog
        open={nameDialog}
        title="New dashboard"
        initialValue="New dashboard"
        confirmLabel="Create"
        onConfirm={(name) => {
          setNameDialog(false);
          createNamed(name);
        }}
        onCancel={() => setNameDialog(false)}
      />

      {/* Inline rename dialog — edits working.name (persists on Save). */}
      {working && (
        <NameDialog
          open={renaming}
          title="Rename dashboard"
          initialValue={working.name}
          confirmLabel="Rename"
          onConfirm={(name) => {
            setRenaming(false);
            updateWorking({ name });
          }}
          onCancel={() => setRenaming(false)}
        />
      )}
    </div>
  );
}
